import { useEffect, useMemo, useRef } from 'react'
import type { AuthRecord } from '../context/authContext'
import {
  getAuthorizationCacheScope,
  getRpcAccess,
  RpcAccessDenied,
} from '../app/AppAuth/rpcAuthorization'

export type CurrentAuth = (() => AuthRecord | null | undefined) & {
  waitForUpdate?: () => Promise<void>
}

type RpcSession = {
  readonly auth: AuthRecord | null | undefined
  isCurrent: () => boolean
  canRecover: (error: { code?: number; message?: string }) => boolean
  acceptRefresh: (accessToken: string) => Promise<void>
}

// Wake pending retries when permissions finish loading or their owner unmounts.
// Reading the ref also handles an update that committed before we subscribed.
export function useCurrentAuth(auth: AuthRecord): CurrentAuth {
  const current = useRef<AuthRecord | null>(auth)
  const listeners = useRef(new Set<() => void>())
  current.current = auth
  useEffect(() => {
    current.current = auth
    listeners.current.forEach((notify) => notify())
    listeners.current.clear()
  }, [auth])
  useEffect(
    () => () => {
      current.current = null
      listeners.current.forEach((notify) => notify())
      listeners.current.clear()
    },
    [],
  )
  return useMemo(
    () =>
      Object.assign(() => current.current, {
        waitForUpdate: () =>
          new Promise<void>((resolve) => listeners.current.add(resolve)),
      }),
    [],
  )
}

const sessionIdentity = (auth?: AuthRecord | null) =>
  JSON.stringify([
    auth?.accountId,
    auth?.email,
    auth?.useCase,
    auth?.isAuthenticated,
    auth?.impersonation?.isImpersonating,
    auth?.impersonation?.accessRoleId,
    auth?.impersonation?.groupIds,
  ])

// Only this operation may adopt the refreshed snapshot. Saved clients and other
// requests retain their original scope and cannot use a revoked grant.
export function createRpcSession(
  initialAuth: AuthRecord | null | undefined,
  currentAuth: CurrentAuth | undefined,
  method: unknown,
  request: unknown,
): RpcSession {
  let auth = initialAuth
  const isCurrent = () =>
    !currentAuth ||
    (currentAuth()?.accessToken === auth?.accessToken &&
      getAuthorizationCacheScope(currentAuth()) ===
        getAuthorizationCacheScope(auth))
  const canRecover = (error: { code?: number; message?: string }) =>
    error?.code === 7 &&
    /Invalid (token|permissions) version/.test(error.message || '') &&
    (!currentAuth ||
      (!!currentAuth() &&
        sessionIdentity(currentAuth()) === sessionIdentity(initialAuth)))

  return {
    get auth() {
      return auth
    },
    isCurrent,
    canRecover,
    async acceptRefresh(accessToken: string) {
      // Non-React callers have no observable session. Preserve their existing
      // retry contract; the server still authorizes the refreshed token.
      if (!currentAuth) {
        auth = auth && { ...auth, accessToken }
        return
      }
      for (;;) {
        const latest = currentAuth()
        if (
          !latest ||
          sessionIdentity(latest) !== sessionIdentity(initialAuth) ||
          (latest.accessToken !== initialAuth?.accessToken &&
            latest.accessToken !== accessToken)
        )
          throw new RpcAccessDenied(method)
        if (latest.accessToken === accessToken && !latest.isAuthLoading) {
          if (latest.permissionsError)
            throw new Error('Unable to reload permissions. Please try again.')
          if (getRpcAccess(method, latest, request) !== 'allowed')
            throw new RpcAccessDenied(method)
          auth = latest
          return
        }
        if (!currentAuth.waitForUpdate) throw new RpcAccessDenied(method)
        await currentAuth.waitForUpdate()
      }
    },
  }
}
